
import {
  Component, Vue, Prop, Ref, Watch,
} from 'vue-property-decorator'
import ViewModel from '@/models/ViewModel'
import DataTable from '@/components/DataTable/index.vue'
import PublisherReconciliation from '@/models/PublisherReconciliation'
import { getModule } from 'vuex-module-decorators'
import SystemtModule from '@/store/SystemModule'
import util from '@/mixins/util'
import WebMessage from '@/models/WebMessage'
import SearchInput from '@/components/SearchInput/SearchInput.vue'
import IconAction from '@/components/IconAction/IconAction.vue'
import Widget from '@/components/Widget/Widget.vue'
import row_fields from '../fields/rows-fields'

@Component({
  mixins: [util],
  components: {
    DataTable,
    Widget,
    SearchInput,
    IconAction,
  },
})
class PublisherReconciliationRowsView extends ViewModel {
  @Ref() readonly dataTable!: HTMLFormElement

  @Prop()
  public billingMonth!: any

  // =====

  public records: number = 0

  public loading: boolean = false

  public ready = false

  public query: string[] = []

  public selected: string[] = []

  public fieldFilters: any = {}

  public query_settings: any = {
    custom_fields: [
      {
        name: 'status:pending',
        value: 'status:pending',
      },
      {
        name: 'status:approved',
        value: 'status:approved',
      },
    ],
  }

  public fields: Array<any> = []

  public sortBy: string = 'billing_month'

  public sortDesc: boolean = true

  public table_data: any = []

  public show_approve_warning: boolean = false

  public zero_impression_rows: any[] = []

  public approve_warning_acknowledged: boolean = false

  public show_zero_row_list: boolean = false

  // IDs checked in the Manage list — these will be excluded from the approve request.
  public excluded_ids: string[] = []

  public fieldFiltersKeys!: (fieldFilters: any) => string[]

  // watch selected
  // @Watch('excluded_ids')
  // onSelectedChange() {

  // }

  // =====

  public get show_fields() {
    return this.fields.filter((f: any) => f.show)
  }

  public get options() {
    return this.table_data
      .filter((r: any) => Number(r.third_party_impressions) === 0)
      .map((r: any, index: number) => ({
        text: `Row #${index + 1} ${r.inventory ? r.inventory.name : 'N/A'} ${
          r.line_item ? `- ${r.line_item.name}` : 'N/A'
        }`,
        value: r.id,
      }))
  }

  public get isPending(): boolean {
    return this.table_data
      .filter(r => this.selected.includes(r.id))
      .some((r: any) => r.status === 'pending')
  }

  public get constainsZeroImpressions(): boolean {
    return this.table_data
      .filter(r => this.selected.includes(r.id))
      .some((r: any) => Number(r.third_party_impressions) === 0)
  }
  // =====

  public mounted() {
    this.fields = row_fields
    this.loadFilters()
  }

  public statusVariant(status: string): string {
    switch (status) {
      case 'approved':
        return 'success'
      case 'pending':
        return 'warning'
      default:
        return 'secondary'
    }
  }

  public async rows(ctx: any): Promise<any> {
    this.loading = true

    try {
      const field_filters = this.fieldFiltersKeys(this.fieldFilters)

      this.syncFilters()

      const result = await PublisherReconciliation.detail(this.billingMonth, {
        page_size: ctx.perPage,
        page: ctx.currentPage,
        order_by: ctx.sortBy ? ctx.sortBy : 'inventory',
        order: ctx.sortDesc ? 'desc' : 'asc',
        query: [...ctx.filter, ...field_filters],
        mode: 'exclusive',
      })
      this.records = result.records
      this.table_data = result.data
      return this.table_data
    } catch (error) {
      console.error('Error loading rows:', error)
    } finally {
      this.loading = false
    }
  }

  public syncFilters() {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'publisher-reconciliation-view',
      data: { query: this.query, fieldFilters: this.fieldFilters },
    })
  }

  public loadFilters() {
    const system = getModule(SystemtModule)
    system
      .getFilter('publisher-reconciliation-view')
      .then((filter: any) => {
        if (filter) {
          this.query = filter.query
          this.fieldFilters = filter.fieldFilters
        }
        this.ready = true
      })
      .finally(() => {
        this.ready = true
      })
  }

  public async reopenRow(row: any): Promise<void> {
    this.loading = true
    try {
      await PublisherReconciliation.reopenRow(row.id)

      WebMessage.success('Row reopened successfully')
      this.refresh()
    } catch (error: any) {
      WebMessage.error('Failed to reopen row')
      // @ts-nocheck
      console.error('Error reopening row:', error)
    } finally {
      this.loading = false
    }
  }

  public async approveRow(row: any): Promise<void> {
    this.loading = true
    try {
      await PublisherReconciliation.approveRow(row.id)

      WebMessage.success('Row approved successfully')
      this.refresh()
    } catch (error: any) {
      WebMessage.error('Failed to approve row')
      // @ts-nocheck
      console.error('Error approving row:', error)
    } finally {
      this.loading = false
    }
  }

  public refresh() {
    this.dataTable.refresh()
  }

  public async bulkApprove(): Promise<void> {
    if (this.constainsZeroImpressions) {
      this.checkBeforeApproving()
      return
    }

    await this.doApprove()
  }

  // Called by modal @ok (after warning acknowledged) and directly when no zero-impression rows.
  public async doApprove(): Promise<void> {
    const idsToApprove = [...this.selected].filter((id: string) => !this.excluded_ids.includes(id))

    const confirmed = async () => {
      this.loading = true
      try {
        await PublisherReconciliation.bulkApprove(idsToApprove)

        this.refresh()
        this.selected = []
      } catch (error: any) {
        WebMessage.error('Failed to approve rows')
        console.error('Error bulk approving rows:', error)
      } finally {
        this.loading = false
      }
    }

    WebMessage.confirm('Do you want to bulk approve the selected rows?', 'Bulk Update', {
      okTitle: 'Approve',
    }).then((value: boolean) => {
      if (value) {
        confirmed()
      }
      return value
    })
  }

  public onApproveWarningHidden(): void {
    this.approve_warning_acknowledged = false
    this.show_zero_row_list = false
    this.excluded_ids = []
  }

  public async bulkReopen(): Promise<void> {
    const confirmed = async () => {
      this.loading = true
      try {
        // selected ids
        await PublisherReconciliation.bulkApprove(this.selected, true)

        WebMessage.success('All rows approved successfully')

        this.refresh()
      } catch (error: any) {
        WebMessage.error('Failed to approve all rows')
        // @ts-nocheck
        console.error('Error bulk approving rows:', error)
      } finally {
        this.loading = false
      }
    }

    WebMessage.confirm(
      `Are you sure you want to reopen the selected ${this.selected.length} rows?`,
      'Bulk Update',
      { okTitle: 'Reopen' },
    ).then((value: boolean) => {
      if (value) {
        confirmed()
      }
      return value
    })
  }

  public async pushApproved(): Promise<void> {
    const confirmed = async () => {
      this.loading = true
      try {
        await PublisherReconciliation.pushApproved(this.billingMonth)

        WebMessage.success('Approved rows pushed successfully')
      } catch (error: any) {
        WebMessage.error('Failed to push approved rows')
        // @ts-nocheck
        console.error('Error pushing approved rows:', error)
      } finally {
        this.loading = false
      }
    }
    WebMessage.confirm(
      'This will push all approved rows. Do you want to continue?',
      'Push Approved Rows',
      { okTitle: 'Push' },
    ).then((value: boolean) => {
      if (value) {
        confirmed()
      }
    })
  }

  public async exportPeriod(): Promise<void> {
    await PublisherReconciliation.exportPeriod(this.billingMonth)
  }

  // Returns false and opens the warning modal when any selected row has zero 3p impressions.
  // Returns true when it's safe to proceed directly to doApprove().
  private checkBeforeApproving(): boolean {
    const zeroRows = this.table_data.filter(
      (r: any) => this.selected.includes(r.id) && Number(r.third_party_impressions) === 0,
    )
    if (zeroRows.length > 0) {
      this.zero_impression_rows = zeroRows
      this.approve_warning_acknowledged = false
      this.excluded_ids = []
      this.show_approve_warning = true
      return false
    }
    return true
  }

  public removeRow(id: string): void {
    this.excluded_ids = [...this.excluded_ids, id]
  }

  public addRow(id: string): void {
    this.excluded_ids = this.excluded_ids.filter(excluded_id => excluded_id !== id)
  }
}
export default PublisherReconciliationRowsView
